// Latest deploy status across all 9 Vercel projects (3 apps × 3 envs).
// Platform-wide — same data on every env tab.
//
// Collapsible: when something is non-green (building, error, etc.) the
// grid auto-expands so the operator sees the per-project detail right
// away. When everything is READY the grid stays collapsed and only the
// per-app summary dots show. The user can toggle either direction.

import { useState } from 'react';

interface DeployState {
  app: 'hub' | 'persona_builder' | 'persona_studio';
  env: 'dev' | 'staging' | 'prod';
  projectName: string;
  state:
    | 'READY'
    | 'ERROR'
    | 'BUILDING'
    | 'QUEUED'
    | 'CANCELED'
    | 'INITIALIZING'
    | 'UNKNOWN';
  url: string | null;
  inspectorUrl: string | null;
  createdAt: number | null;
  readyAt: number | null;
  buildingDurationMs: number | null;
  commitSha: string | null;
  commitMessage: string | null;
  branch: string | null;
  error?: string;
}

interface Props {
  configured: boolean;
  reason?: string;
  deploys: DeployState[];
}

type AppKey = DeployState['app'];
type RollupTone = 'green' | 'yellow' | 'red' | 'gray';

const APP_LABEL: Record<AppKey, string> = {
  hub: 'Hub',
  persona_builder: 'Builder',
  persona_studio: 'Studio',
};

const ENV_LABEL: Record<DeployState['env'], string> = {
  dev: 'Dev',
  staging: 'Staging',
  prod: 'Prod',
};

// Aggregate the per-env states of one app into a single status indicator.
// Worst-state wins: any ERROR → red; any in-flight (building/queued/init)
// → yellow; all READY → green; anything else (CANCELED, UNKNOWN) → gray.
function appTone(rows: DeployState[]): RollupTone {
  if (rows.some((d) => d.state === 'ERROR')) return 'red';
  if (rows.some((d) => d.state === 'BUILDING' || d.state === 'QUEUED' || d.state === 'INITIALIZING')) return 'yellow';
  if (rows.length > 0 && rows.every((d) => d.state === 'READY')) return 'green';
  return 'gray';
}

const DOT_CLASS: Record<RollupTone, string> = {
  green: 'bg-emerald-400',
  yellow: 'bg-amber-400',
  red: 'bg-rose-500',
  gray: 'bg-slate-600',
};

export function DeployGrid({ configured, reason, deploys }: Props) {
  const errorCount = deploys.filter((d) => d.state === 'ERROR').length;
  const buildingCount = deploys.filter((d) =>
    ['BUILDING', 'QUEUED', 'INITIALIZING'].includes(d.state)
  ).length;
  const allGreen = errorCount === 0 && buildingCount === 0 && deploys.every((d) => d.state === 'READY');

  // Default expansion: collapsed when everything is healthy (boring case),
  // expanded when anything needs attention (interesting case) — so the
  // operator's eye lands on the detail when there's something to see.
  // Hook must run before the not-configured early return (React error #310).
  const [expanded, setExpanded] = useState(!allGreen);

  if (!configured) {
    return (
      <section className="border border-slate-800 rounded-lg p-4">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300 mb-2">
          Deploys
        </h3>
        <p className="text-xs text-slate-500 italic">
          {reason || 'Vercel API not configured on this hub deployment.'}
        </p>
      </section>
    );
  }

  // Per-app rollup (used both for the collapsed summary and to order the
  // expanded columns consistently).
  const byApp: Record<AppKey, DeployState[]> = {
    hub: [],
    persona_builder: [],
    persona_studio: [],
  };
  for (const d of deploys) byApp[d.app].push(d);

  const summaries: Array<{ app: AppKey; tone: RollupTone; rows: DeployState[] }> = (
    ['hub', 'persona_builder', 'persona_studio'] as const
  ).map((app) => ({
    app,
    tone: appTone(byApp[app]),
    rows: byApp[app],
  }));

  return (
    <section className="border border-slate-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full bg-slate-900/40 px-4 py-2.5 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap text-left hover:bg-slate-900/60 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span
            className={`text-[10px] text-slate-500 transition-transform inline-block ${
              expanded ? 'rotate-90' : ''
            }`}
            aria-hidden
          >
            ▶
          </span>
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
            Deploys (latest production per Vercel project)
          </h3>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-500 font-mono">
          {!expanded && (
            <div className="flex items-center gap-3">
              {summaries.map((s) => (
                <span key={s.app} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${DOT_CLASS[s.tone]}`} />
                  <span className="text-slate-300">{APP_LABEL[s.app]}</span>
                </span>
              ))}
            </div>
          )}
          {expanded && (
            <span>
              {errorCount > 0 && (
                <span className="text-rose-400 font-bold">{errorCount} error · </span>
              )}
              {buildingCount > 0 && (
                <span className="text-amber-300">{buildingCount} building · </span>
              )}
              {deploys.length - errorCount - buildingCount} ready
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          {summaries.map(({ app, rows }) => (
            <div key={app} className="space-y-2">
              <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">
                {APP_LABEL[app]}
              </div>
              {rows.map((d) => (
                <DeployCard key={`${d.app}:${d.env}`} d={d} />
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DeployCard({ d }: { d: DeployState }) {
  const tone =
    d.state === 'READY'
      ? 'border-emerald-800/40 bg-emerald-950/10'
      : d.state === 'ERROR'
        ? 'border-rose-800/40 bg-rose-950/10'
        : d.state === 'BUILDING' || d.state === 'QUEUED' || d.state === 'INITIALIZING'
          ? 'border-amber-800/40 bg-amber-950/10'
          : 'border-slate-800 bg-slate-950/40';
  const stateBadge =
    d.state === 'READY'
      ? 'bg-emerald-900/30 text-emerald-300 border-emerald-700/40'
      : d.state === 'ERROR'
        ? 'bg-rose-900/30 text-rose-300 border-rose-700/40'
        : d.state === 'BUILDING' || d.state === 'QUEUED' || d.state === 'INITIALIZING'
          ? 'bg-amber-900/30 text-amber-300 border-amber-700/40'
          : 'bg-slate-800 text-slate-400 border-slate-700';

  const commitShort = d.commitSha ? d.commitSha.slice(0, 7) : null;
  const commitFirstLine = (d.commitMessage ?? '').split('\n')[0];
  const duration = d.buildingDurationMs ? `${Math.round(d.buildingDurationMs / 1000)}s` : null;

  return (
    <a
      href={d.inspectorUrl || d.url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className={`block rounded border ${tone} p-2.5 hover:bg-slate-900/40 transition-colors`}
      title={d.error ? d.error : commitFirstLine || ''}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
          {ENV_LABEL[d.env]}
        </span>
        <span
          className={`text-[9px] uppercase tracking-widest font-bold border rounded px-1.5 py-0.5 ${stateBadge}`}
        >
          {d.state}
        </span>
      </div>
      <div className="text-[11px] text-slate-300 truncate">
        {commitFirstLine || (d.error ? <span className="text-rose-300">{d.error}</span> : '—')}
      </div>
      <div className="flex items-center justify-between gap-2 mt-1 text-[10px] text-slate-500 font-mono">
        <span className="truncate">
          {commitShort && <>{commitShort} · </>}
          {d.branch || ''}
        </span>
        <span className="whitespace-nowrap">
          {duration && <>{duration} · </>}
          {d.readyAt ? formatRel(d.readyAt) : d.createdAt ? formatRel(d.createdAt) : '—'}
        </span>
      </div>
    </a>
  );
}

function formatRel(ms: number): string {
  const diff = Math.max(0, Date.now() - ms);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ms).toLocaleDateString();
}
